import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  TrackingActions,
  TrackingBarFill,
  TrackingButtonPrimary,
  TrackingButtonSecondary,
  TrackingCard,
  TrackingDatePicker,
  TrackingField,
  TrackingForm,
  TrackingInput,
  TrackingLabel,
} from '../common/TrackingShared';

export const colors = {
  border: '#E1E8ED',
  textMuted: '#666666',
  accent: 'var(--vert-vitalite)',
  primary: 'var(--bleu-mineral)',
  danger: 'var(--rouge-alerte)',
  warning: 'var(--jaune-energie)',
  success: 'var(--vert-vitalite)',
};

export const trackingTokens = {
  color: {
    border: '#E1E8ED',
    borderSoft: '#D6DEE6',
    surface: '#FFFFFF',
    surfaceAlt: '#F8F9FA',
    text: 'var(--bleu-mineral)',
    textMuted: '#666666',
    accent: 'var(--vert-vitalite)',
    danger: 'var(--rouge-alerte)',
    editBorder: 'var(--jaune-energie)',
    editBg: '#FFF8E1',
  },
};

export const JournalHeader = styled.div`
  background: linear-gradient(135deg, var(--bleu-mineral) 0%, #2c5282 100%);
  border-radius: 12px;
  padding: 1rem;
  box-shadow: 0 6px 20px rgba(26, 46, 64, 0.15);
  position: relative;
  overflow: visible;

  font-family: 'Poppins', sans-serif;
  font-size: 1.1rem;
  font-weight: 700;
  color: #ffffff;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
  letter-spacing: 0.5px;
  line-height: 1.2;
  z-index: 0;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: inherit;
    background: linear-gradient(45deg, rgba(255, 255, 255, 0.1) 25%, transparent 25%),
      linear-gradient(-45deg, rgba(255, 255, 255, 0.1) 25%, transparent 25%),
      linear-gradient(45deg, transparent 75%, rgba(255, 255, 255, 0.1) 75%),
      linear-gradient(-45deg, transparent 75%, rgba(255, 255, 255, 0.1) 75%);
    background-size: 15px 15px;
    background-position: 0 0, 0 7.5px, 7.5px -7.5px, -7.5px 0;
    opacity: 0.2;
    pointer-events: none;
  }

  & > * {
    position: relative;
    z-index: 1;
    margin: 0;
  }
`;

export const SectionCard = styled.section.attrs({ className: 'meal-section' })`
  width: 100%;
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;
  background: transparent !important;
  border: 0 !important;
  box-shadow: none !important;
  padding: 0 !important;
`;

export const SectionBody = styled.div`
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.7rem;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  min-width: 0;
  overflow-x: clip;
  background: #FFFFFF;
  border: 1px solid #E1E8ED;
  border-radius: 12px;
  box-shadow: var(--shadow-md);

  @media (max-width: 768px) {
    padding: 0.82rem;
    gap: 0.6rem;
  }
`;

export const SettingsSectionCard = styled.section`
  background: white;
  border-radius: 14px;
  overflow: hidden;
  box-shadow: 0 8px 18px rgba(26, 46, 64, 0.09);
  margin-bottom: 1rem;

  @media (max-width: 1024px) {
    border-radius: 10px;
    box-shadow: 0 2px 8px rgba(26, 46, 64, 0.08);
    margin-bottom: 0.7rem;
  }
`;

export const SettingsSectionHeader = styled.div`
  background: linear-gradient(135deg, #2c5282 0%, #1A2E40 50%, #1a365d 100%);
  color: white;
  padding: 0.95rem 1rem;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(45deg, rgba(255, 255, 255, 0.1) 25%, transparent 25%),
      linear-gradient(-45deg, rgba(255, 255, 255, 0.1) 25%, transparent 25%),
      linear-gradient(45deg, transparent 75%, rgba(255, 255, 255, 0.1) 75%),
      linear-gradient(-45deg, transparent 75%, rgba(255, 255, 255, 0.1) 75%);
    background-size: 12px 12px;
    background-position: 0 0, 0 6px, 6px -6px, -6px 0;
    opacity: 0.18;
    pointer-events: none;
  }

  @media (max-width: 1024px) {
    padding: 0.72rem 0.82rem;
  }
`;

export const SettingsSectionTitle = styled.h2`
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.45rem;
  font-size: 1.05rem;
  font-family: 'Poppins', 'Roboto', sans-serif;
  position: relative;
  z-index: 1;

  @media (max-width: 1024px) {
    font-size: 0.95rem;
  }
`;

export const SettingsSectionBody = styled.div`
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.85rem;

  @media (max-width: 1024px) {
    padding: 0.72rem;
    gap: 0.68rem;
  }
`;

export const SettingsSectionFooter = styled.div`
  padding: 0.9rem 1rem;
  background: #fafbfc;
  border-top: 1px solid #e1e5e9;
  display: flex;
  justify-content: flex-end;
  gap: 0.55rem;
  flex-wrap: wrap;

  @media (max-width: 1024px) {
    padding: 0.7rem;
    justify-content: stretch;
  }
`;

export const DangerZone = styled.div`
  border: 1px solid #f1c0c0;
  background: linear-gradient(135deg, #fff5f5, #ffffff);
  border-radius: 12px;
  padding: 0.85rem;

  @media (max-width: 1024px) {
    border-radius: 10px;
    padding: 0.68rem;
  }
`;

export const DangerTitle = styled.div`
  color: #8b2f2f;
  font-size: 0.82rem;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

export const DangerText = styled.div`
  margin-top: 0.35rem;
  color: #7a3b3b;
  font-size: 0.86rem;
  line-height: 1.45;
  font-weight: 650;
`;

export const DangerActions = styled.div`
  margin-top: 0.65rem;
`;

export const FooterButton = styled.button`
  border: 2px solid ${(props) => (props.$variant === 'outline' ? '#4CAF50' : 'transparent')};
  background: ${(props) => (props.$variant === 'outline' ? '#ffffff' : '#4CAF50')};
  color: ${(props) => (props.$variant === 'outline' ? '#4CAF50' : '#ffffff')};
  border-radius: 8px;
  min-height: 42px;
  padding: 0.55rem 1rem;
  font-size: 0.9rem;
  font-weight: 700;
  cursor: pointer;
  box-shadow: ${(props) => (props.$variant === 'outline' ? 'none' : '0 4px 10px rgba(76, 175, 80, 0.24)')};

  &:disabled {
    opacity: 0.65;
    cursor: not-allowed;
    box-shadow: none;
  }

  @media (max-width: 1024px) {
    width: 100%;
    justify-content: center;
  }
`;

export const DangerButton = styled(FooterButton)`
  background: #e53935;
  box-shadow: 0 4px 10px rgba(229, 57, 53, 0.2);
`;

// Tracking-specific styles
export const EditBanner = styled.div`
  border: 1px solid rgba(255, 255, 255, 0.22);
  background: linear-gradient(135deg, var(--bleu-mineral) 0%, #2c5282 100%);
  border-radius: 12px;
  padding: 0.7rem 0.85rem;
  color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.7rem;
  flex-wrap: wrap;
`;

export const EditBannerText = styled.div`
  font-size: 0.88rem;
  font-weight: 700;
`;

export const EditBannerSubtext = styled.div`
  margin-top: 0.12rem;
  font-size: 0.78rem;
  color: rgba(255, 255, 255, 0.9);
  font-weight: 600;
`;

export const EditBannerActions = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    width: 100%;
    display: flex;
    flex-direction: column-reverse;
    align-items: stretch;
    justify-content: stretch;

    button {
      width: 100%;
    }
  }
`;

export const EntryPanel = styled(TrackingCard)`
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
`;

export const EntryActions = styled(TrackingActions)`
  justify-content: flex-end;
  margin-top: 0.1rem;
`;

export const EntryPanelHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

export const EntryPanelTitle = styled.div`
  color: var(--bleu-mineral);
  font-size: 0.82rem;
  font-weight: 800;
  letter-spacing: 0.01em;
`;

export const EntryPanelHint = styled.div`
  color: #666666;
  font-size: 0.8rem;
  font-weight: 600;
`;

export const OverviewHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.7rem;
  flex-wrap: wrap;
`;

export const OverviewMeta = styled.div`
  display: inline-flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.35rem;
`;

export const OverviewMetaItem = styled.span`
  display: inline-flex;
  align-items: center;
  border: 1px solid #dbe7f3;
  background: #f7fbff;
  border-radius: 999px;
  padding: 0.15rem 0.5rem;
  color: #55738d;
  font-size: 0.72rem;
  font-weight: 700;
`;

export const OverviewBarValue = styled.div`
  margin-bottom: 0.16rem;
  color: #2b4f6d;
  font-size: 0.72rem;
  font-weight: 800;
  line-height: 1;
`;

export const OverviewBarFill = styled(TrackingBarFill)`
  background: linear-gradient(180deg, #6dc66c 0%, var(--vert-vitalite) 62%, #2b8b49 100%);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.3);
`;

export const Grid = styled(TrackingForm)``;

export const Field = styled(TrackingField)``;

export const Label = styled(TrackingLabel)``;

export const Input = styled(TrackingInput)``;

export const EntryDatePicker = styled(TrackingDatePicker)``;

export const NoteEditorForm = styled.form`
  padding: 0;
  width: 100%;
  display: flex;
  flex-direction: column;

  @media (max-width: 576px) {
    padding: 0;
  }

  @media (max-width: 400px) {
    padding: 0;
  }
`;

export const NoteFormGroup = styled.div`
  margin-bottom: 0;

  @media (max-width: 576px) {
    margin-bottom: 1rem;
  }
`;

export const NoteLabel = styled.label`
  display: flex;
  align-items: center;
  gap: clamp(0.35rem, 1.5vw, 0.5rem);
  font-family: 'Poppins', sans-serif;
  font-size: clamp(0.8rem, 2.5vw, 0.95rem);
  font-weight: 600;
  color: #1A2E40;
  margin-bottom: clamp(0.4rem, 1.5vw, 0.6rem);
  flex-wrap: wrap;
  line-height: 1.3;

  @media (max-width: 576px) {
    font-size: 0.9rem;
    gap: 0.4rem;
    margin-bottom: 0.5rem;
  }

  @media (max-width: 400px) {
    font-size: 0.85rem;
    gap: 0.35rem;
    margin-bottom: 0.4rem;
  }
`;

export const NoteLabelIcon = styled(FontAwesomeIcon)`
  color: #FFC107;
  font-size: clamp(0.9rem, 3vw, 1.1rem);
  flex-shrink: 0;

  @media (max-width: 576px) {
    font-size: 1rem;
  }

  @media (max-width: 400px) {
    font-size: 0.9rem;
  }
`;

export const NoteLabelText = styled.span`
  word-break: break-word;
  max-width: 100%;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

export const NoteTextarea = styled.textarea`
  width: 100%;
  padding: clamp(0.7rem, 2.5vw, 1rem);
  border: 2px solid ${(props) => (props.$isFocused ? '#FFC107' : '#e5e7eb')};
  border-radius: clamp(8px, 2vw, 10px);
  font-size: clamp(0.85rem, 2.2vw, 0.95rem);
  font-family: 'Roboto', sans-serif;
  resize: vertical;
  min-height: clamp(128px, 22vh, 180px);
  max-height: min(48dvh, 360px);
  line-height: 1.5;
  transition: border-color 0.2s ease;
  box-sizing: border-box;
  touch-action: manipulation;

  @media (max-width: 576px) {
    padding: 0.75rem;
    border-radius: 8px;
    font-size: 16px;
    min-height: 128px;
    max-height: min(42dvh, 300px);
  }

  @media (max-width: 400px) {
    padding: 0.65rem;
    border-radius: 6px;
    font-size: 16px;
    min-height: 116px;
    max-height: min(36dvh, 240px);
  }

  &::placeholder {
    color: #9ca3af;
  }

  &:focus {
    outline: none;
    border-color: #FFC107;
    box-shadow: 0 0 0 3px rgba(255, 193, 7, 0.1);
  }
`;

export const NoteCharCount = styled.small`
  color: ${(props) => (props.$isNearLimit ? '#dc3545' : '#6c757d')};
  font-size: clamp(0.7rem, 2vw, 0.8rem);
  margin-top: clamp(0.25rem, 1vw, 0.35rem);
  display: block;
  text-align: right;
  font-weight: ${(props) => (props.$isNearLimit ? 600 : 400)};

  @media (max-width: 576px) {
    font-size: 0.75rem;
    margin-top: 0.3rem;
  }

  @media (max-width: 400px) {
    font-size: 0.7rem;
    margin-top: 0.25rem;
  }
`;

export const SleepTrackingCard = styled(TrackingCard)`
  margin-bottom: 1rem;
`;

export const SleepTrackingHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.9rem;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

export const SleepTrackingTitle = styled.h3`
  color: var(--bleu-mineral);
  font-size: 0.95rem;
  font-weight: 700;
  margin: 0;
`;

export const SleepTrackingStatus = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

export const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  border-radius: 999px;
  border: 1px solid ${(props) => (props.$active ? 'var(--vert-vitalite)' : trackingTokens.color.border)};
  background: ${(props) => (props.$active ? '#E8F5E9' : '#FFFFFF')};
  color: ${(props) => (props.$active ? '#2E7D32' : trackingTokens.color.textMuted)};
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  padding: 0.2rem 0.5rem;

  &::before {
    content: '';
    width: 6px;
    height: 6px;
    background: ${(props) => (props.$active ? '#4CAF50' : trackingTokens.color.textMuted)};
    border-radius: 50%;
    ${(props) => props.$active && 'animation: pulse 2s infinite;'}
  }

  @keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.5; }
  }
`;

export const SleepTrackingActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  flex-wrap: wrap;
  width: 100%;
  margin-bottom: 0.5rem;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

export const JournalActionButton = styled.button`
  border: 1px solid #c8d9ea;
  background: #ffffff;
  color: #17466a;
  font-weight: 700;
  font-size: 0.8rem;
  cursor: pointer;
  padding: 0.24rem 0.66rem;
  border-radius: 999px;
  text-decoration: none;
  line-height: 1.2;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  white-space: nowrap;

  &:hover:not(:disabled) {
    color: #113a59;
    border-color: #a9c0d6;
    background: #f5f9fd;
    text-decoration: none;
  }

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
    background: #f2f5f8;
  }

  svg {
    font-size: 0.8rem;
  }
`;

export const PrimaryActionButton = styled(JournalActionButton)`
  background: #eef8f1;
  border-color: #b9d8c0;
  color: #22663f;

  &:hover:not(:disabled) {
    color: #1a5734;
    border-color: #9ec6a8;
    background: #ebf8ef;
  }
`;

export const DangerActionButton = styled(JournalActionButton)`
  background: #FFEBEE;
  border-color: rgba(185, 28, 28, 0.25);
  color: #b91c1c;

  &:hover:not(:disabled) {
    background: #FFCDD2;
    border-color: rgba(185, 28, 28, 0.35);
  }
`;

export const SleepTrackingHint = styled.p`
  color: ${trackingTokens.color.textMuted};
  font-size: 0.8rem;
  margin: 0;
  line-height: 1.4;
`;

export const Footer = styled(TrackingActions)``;

export const SaveButton = styled(TrackingButtonPrimary)`
  @media (max-width: 1024px) {
    min-height: 40px;
    padding: 0.45rem 0.62rem;
    font-size: 0.82rem;
  }
`;

export const SecondaryButton = styled(TrackingButtonSecondary)`
  @media (max-width: 1024px) {
    min-height: 40px;
    padding: 0.45rem 0.62rem;
    font-size: 0.82rem;
  }
`;

export const SessionActionButton = styled.button`
  border: 1px solid ${(props) => (props.$variant === 'danger' ? 'var(--rouge-alerte)' : '#E1E8ED')};
  background: ${(props) => (props.$variant === 'danger' ? '#FFEBEE' : '#FFFFFF')};
  color: ${(props) => (props.$variant === 'danger' ? '#C62828' : 'var(--bleu-mineral)')};
  border-radius: 9px;
  min-height: 36px;
  padding: 0.3rem 0.55rem;
  font-size: 0.74rem;
  font-weight: 800;
  cursor: pointer;
  transition: filter 0.2s ease;
  min-width: 80px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

export const SessionMain = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.22rem;
`;

export const SessionRatingRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.18rem;
  margin-top: 0.04rem;
`;

export const SessionRatingDot = styled.span`
  color: ${(props) => (props.$active ? 'var(--jaune-energie)' : '#E1E8ED')};
  font-size: 1.05rem;
  line-height: 1;

  &::before {
    content: '★';
    font-size: 1.75rem;
  }
`;

export const SessionNote = styled.div`
  border: 1px solid #dce6f0;
  border-radius: 10px;
  background: #ffffff;
  padding: 0.6rem 0.75rem;
`;

export const SessionNoteLabel = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.8rem;
  font-weight: 700;
  color: #5f7286;
  text-transform: uppercase;
  letter-spacing: 0.02em;

  svg {
    color: #f1b900;
  }
`;

export const SessionNoteText = styled.p`
  margin: 0.42rem 0 0;
  white-space: pre-wrap;
  color: #33475b;
  line-height: 1.48;
  font-size: 0.9rem;
  word-break: break-word;
`;

export const EmptyState = styled.div`
  color: #666666;
  font-size: 0.84rem;
  font-weight: 600;
`;

export const HistoryPaginationBar = styled.div`
  margin-top: 0.55rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.4rem;
`;

export const HistoryMetaText = styled.div`
  color: #6a7f92;
  font-size: 0.76rem;
  font-weight: 700;
  text-align: center;
`;

export const HistoryLoadMoreButton = styled.button`
  border: 1px solid #d3deea;
  background: #ffffff;
  color: var(--bleu-mineral);
  border-radius: 999px;
  min-height: 34px;
  padding: 0.3rem 0.8rem;
  font-size: 0.76rem;
  font-weight: 800;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: #f3f8fd;
    border-color: #b5c8da;
  }

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;
